import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useMemo,
} from "react";
import classnames from "classnames";

import Portal from "../Portal/Portal";

import { EL_ID_PORTAL_MAIN_AFTER } from "../Portal/PortalMainAfter/constants";
import { clutch } from "../../utils/number/number";
import { getBrowserScrollbarInfo, getEventCoordinates } from "@/shared/utils/dom";
import { handleReactRef } from "@/shared/utils/react";

import styles from "./FloatWindow.module.scss";

const isEventContainsDraggableElement = (e) => {
  return (
    "draggable" in e.target.dataset && e.target.dataset.draggable !== "false"
  ) || (e.target.closest("[data-draggable]:not([data-draggable='false'])"));
};

const TIME_CLICK_ALLOWED = 250;

const FloatWindow = forwardRef(({
  className,
  positionInitial,
  children,
  position,
  onChangePosition,
  onDrag,
}, ref) => {
  // FIXES:
  // In Lite App "init drag use effect" is not re-called on page change
  // if dependency is an empty array.
  // Listeners of {elRoot} are not saved on page change either.
  const [elRoot, setElRoot] = useState();
  const [timeStampStart, setTimeStampStart] = useState(undefined);
  const [positionStart, setPositionStart] = useState(undefined);
  const [positionLocal, setPositionLocal] = useState(positionInitial ?? position);

  const positionFinal = position ?? positionLocal;
  const setPositionFinal = onChangePosition ?? setPositionLocal;

  const scrollBarInfo = useMemo(() => getBrowserScrollbarInfo(), []);

  const refPositionFinal = useRef();
  refPositionFinal.current = positionFinal;
  const refIsLastInteractionDragged = useRef();
  const refFnOnDrag = useRef();
  refFnOnDrag.current = onDrag;

  const getRectParent = () => {
    return {
      x: 0,
      y: 0,
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth - scrollBarInfo.width,
      height: window.innerHeight,
    }
  };

  const handleTouchEnd = () => {
    setPositionStart(undefined);
    setTimeStampStart(undefined);
  };

  const handleMouseUp = () => {
    setPositionStart(undefined);
    setTimeStampStart(undefined);
  };

  const onChildResized = (deltaX, deltaY) => {
    const rectParent = getRectParent();
    const rectRoot = elRoot.getBoundingClientRect();

    const topCurrent = refPositionFinal.current?.top;
    const leftCurrent = refPositionFinal.current?.left;

    setPositionFinal({
      top: clutch(
        topCurrent + deltaY,
        rectParent.top,
        rectParent.top + rectParent.height - rectRoot.height,
      ),
      left: clutch(
        leftCurrent + deltaX,
        rectParent.left,
        rectParent.left + rectParent.width - rectRoot.width,
      ),
    });
  };

  const refValue = useRef({});

  refValue.current.onChildResized = onChildResized;

  handleReactRef(ref, refValue.current);

  // Listen to "drag init".
  useEffect(() => {
    const initDragAndDrop = (e) => {
      if (!isEventContainsDraggableElement(e)) {
        return;
      }

      setPositionStart(getEventCoordinates(e));
      setTimeStampStart(e.timeStamp);
    };
    const handleClick = (e) => {
      if (!isEventContainsDraggableElement(e)) {
        return;
      }

      if (refIsLastInteractionDragged.current) {
        e.preventDefault();
        e.stopPropagation();
        refIsLastInteractionDragged.current = undefined;
      }
    };

    if (elRoot) {
      elRoot.addEventListener("touchstart", initDragAndDrop);
      elRoot.addEventListener("mousedown", initDragAndDrop);
      elRoot.addEventListener("click", handleClick);
    }

    return () => {
      if (elRoot) {
        elRoot.removeEventListener("touchstart", initDragAndDrop);
        elRoot.removeEventListener("mousedown", initDragAndDrop);
        elRoot.removeEventListener("click", handleClick);
      }
    };
  }, [elRoot]);

  // Listen to "drag move".
  useEffect(() => {
    let offsetX;
    let offsetY;

    const handleNewPosition = (position) => {
      const rectParent = getRectParent();
      const rectRoot = elRoot.getBoundingClientRect();

      setPositionFinal({
        top: clutch(position.y + offsetY, 0, rectParent.top + rectParent.height - rectRoot.height),
        left: clutch(position.x + offsetX, 0, rectParent.left + rectParent.width - rectRoot.width),
      });
    };
    const handleDrag = (e) => {
      if (e.timeStamp - timeStampStart > TIME_CLICK_ALLOWED) {
        refIsLastInteractionDragged.current = true;
      }

      handleNewPosition(getEventCoordinates(e));

      if (refFnOnDrag.current) {
        refFnOnDrag.current(e);
      }
    };

    if (positionStart && elRoot) {
      const rootBoundingClientRect = elRoot.getBoundingClientRect();
      offsetX = rootBoundingClientRect.left - positionStart.x;
      offsetY = rootBoundingClientRect.top - positionStart.y;

      window.addEventListener("mousemove", handleDrag);
      window.addEventListener("touchmove", handleDrag);
    }

    return () => {
      if (positionStart) {
        window.removeEventListener("mousemove", handleDrag);
        window.removeEventListener("touchmove", handleDrag);
      }
    };
  }, [positionStart, timeStampStart]);


  // Validate {position} on "resize".
  useEffect(() => {
    let windowWidthPrev = window.innerWidth;
    let windowHeightPrev = window.innerHeight;
    const validatePosition = () => {
      if (elRoot) {
        const rectParent = getRectParent();
        const rectRoot = elRoot.getBoundingClientRect();

        const topCurrent = refPositionFinal.current?.top;
        const leftCurrent = refPositionFinal.current?.left;

        setPositionFinal({
          top: clutch(
            (topCurrent > windowHeightPrev / 2)
              ? topCurrent + (window.innerHeight - windowHeightPrev)
              : topCurrent,
            rectParent.top,
            rectParent.top + rectParent.height - rectRoot.height,
          ),
          left: clutch(
            (leftCurrent > windowWidthPrev / 2)
              ? leftCurrent + (window.innerWidth - windowWidthPrev)
              : leftCurrent,
            rectParent.left,
            rectParent.left + rectParent.width - rectRoot.width,
          ),
        });
      }

      windowWidthPrev = window.innerWidth;
      windowHeightPrev = window.innerHeight;
    };

    window.addEventListener("resize", validatePosition);

    validatePosition();

    return () => {
      window.removeEventListener("resize", validatePosition);
    };
  }, [elRoot]);

  return (
    <Portal id={EL_ID_PORTAL_MAIN_AFTER}>
      <div
        ref={setElRoot}
        className={classnames(styles["float-window"], className, {
          [styles["dragging"]]: !!positionFinal,
        })}
        style={positionFinal}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        tabIndex={0}
      >
        {children}
      </div>
    </Portal>
  );
});

export default FloatWindow;
